import React from "react";
import { Box, Typography, IconButton, Divider } from "@mui/material";
import { Repeat, Timer, X, ChevronRight } from "lucide-react";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { colorSystem } from "../../theme";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: colorSystem.blue5,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(2),
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column",
  },
  divide: {
    marginTop: theme.spacing(1.5),
  },
  footer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    marginTop: theme.spacing(1.5),
  },
  footerItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  icon: {
    width: 16,
    height: 16,
    color: theme.palette.text.secondary,
  },
  actionButton: {
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5),
    width: 32,
    height: 32,
  },
}));

export type WorkoutCardBaseProps = {
  title: string;
  subtitle?: React.ReactNode;
  onClick?: () => void;
  type?: "arrow" | "close" | "none";
  showDivider?: boolean;
  onClose?: () => void;
  footerItem?: {
    session?: number | null;
    min?: number | null;
  };
  className?: string;
  sx?: object;
};

const WorkoutCardBase: React.FC<WorkoutCardBaseProps> = ({
  title,
  subtitle,
  onClick,
  type = "arrow",
  showDivider = true,
  onClose,
  footerItem,
  className,
  sx,
}) => {
  const s = useStyles();

  const hasSession = footerItem?.session != null;
  const hasMin = footerItem?.min != null;
  const hasFooter = hasSession || hasMin;

  return (
    <Box className={clsx(s.root, className)} sx={sx} onClick={onClick}>
      <Box className={s.header}>
        <Box className={s.titleBlock}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>

          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        {type === "arrow" && <ChevronRight className={s.actionButton} />}
        {type === "close" && (
          <IconButton
            className={s.actionButton}
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
          >
            <X size={24} strokeWidth={2} />
          </IconButton>
        )}
      </Box>

      {showDivider && <Divider className={s.divide} />}

      {hasFooter && (
        <Box className={s.footer}>
          {hasSession && (
            <Typography variant="body2" className={s.footerItem}>
              <Repeat className={s.icon} />
              {`${footerItem!.session} sets`}
            </Typography>
          )}
          {hasMin && (
            <Typography variant="body2" className={s.footerItem}>
              <Timer className={s.icon} />
              {`${footerItem!.min} min`}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WorkoutCardBase;
